import { useSyncExternalStore } from 'react'

export interface VisualViewportBox {
  /** Height of the area actually visible to the user, in CSS pixels. */
  height: number
  /** Distance between the top of the layout viewport and the visible area. */
  offsetTop: number
}

/**
 * iOS Safari does not shrink the layout viewport when the on-screen keyboard
 * opens, so a `position: fixed` full-height panel keeps running behind the
 * keyboard and its bottom-anchored input becomes untypeable. Safari's
 * scroll-the-field-into-view does not help there either, precisely because the
 * panel is fixed. The visual viewport is the only API that reports the
 * shrunken box, so sizing such a panel to it is the reliable fix.
 */

// Cached so `getSnapshot` returns a referentially stable value; returning a
// fresh object on every call would loop useSyncExternalStore forever.
let snapshot: VisualViewportBox | null = null

function getSnapshot(): VisualViewportBox | null {
  const viewport = typeof window === 'undefined' ? undefined : window.visualViewport
  if (!viewport) return null
  if (
    !snapshot ||
    snapshot.height !== viewport.height ||
    snapshot.offsetTop !== viewport.offsetTop
  ) {
    snapshot = { height: viewport.height, offsetTop: viewport.offsetTop }
  }
  return snapshot
}

function getServerSnapshot(): VisualViewportBox | null {
  return null
}

function subscribe(onChange: () => void): () => void {
  const viewport = typeof window === 'undefined' ? undefined : window.visualViewport
  if (!viewport) return () => {}
  // `scroll` as well as `resize`: iOS reports a keyboard-driven shift of the
  // visible area as a scroll of the visual viewport, not as a resize.
  viewport.addEventListener('resize', onChange)
  viewport.addEventListener('scroll', onChange)
  return () => {
    viewport.removeEventListener('resize', onChange)
    viewport.removeEventListener('scroll', onChange)
  }
}

const subscribeNever = () => () => {}
const getNothing = () => null

/**
 * The current visual viewport box, or `null` where the API is unavailable
 * (jsdom, older browsers) — callers then fall back to their CSS height.
 *
 * Pass `active: false` while the panel is closed. Every page scroll on a phone
 * moves the visual viewport (the URL bar collapses), and the caller is mounted
 * app-wide, so an always-on subscription would re-render it on every scroll.
 */
export function useVisualViewportBox(active: boolean): VisualViewportBox | null {
  return useSyncExternalStore(
    active ? subscribe : subscribeNever,
    active ? getSnapshot : getNothing,
    getServerSnapshot,
  )
}
